import { DOLLAR_TBD } from "api-types/builders/salesConfig";
import type { Doc } from "building-types/doc";
import { EMPTY_INSTANCE_OPTION_SETS } from "building-types/doc/docDefaultConstants";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOption, DocOptionSet, InstanceOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import { formatPricePlusMinus } from "common/utils/formatDollars";
import { isBooleanOptionSet } from "doc/options/optionSetBaseUtils";
import { optionPrice } from "doc/options/optionSetTools";
import type { OptionsState } from "doc/options/optionsStateCore";
import { calculateOptionsState } from "doc/options/optionsStateCore";
import { prepareShowroomOptionChoices } from "doc/options/showroomOptionChoiceProcessing";

/**
 * Extra price from active "GENERATE" linked options that are configured to display against this
 * option's set — these are linked options that add a *new* priced option when its condition matches
 * (rather than modifying an existing option), e.g. choosing a gas range generates an "LP Conversion
 * Kit" at +$250. Its `targetOptionSetId` picks which set's row shows that add-on inline, so we sum
 * every active generate effect aimed at this option's set. Prices are evaluated as if this option
 * were selected, so the inline total matches the cart.
 */
export function getGenerateIntersectionPriceDelta(
  optionsState: OptionsState,
  option: DocOption,
  optionChoices: OptionChoices,
): number {
  const hypotheticalChoices = { ...optionChoices, [option.parentId]: option.id };
  let delta = 0;
  for (const generateEffect of optionsState.generateEffects) {
    if (generateEffect.effect.generateSettings?.targetOptionSetId === option.parentId) {
      delta += optionPrice(generateEffect.option, hypotheticalChoices);
    }
  }
  return delta;
}

export function getTbdPriceLabelOmitDollarPrefix(tbdLabel = DOLLAR_TBD) {
  if (tbdLabel === undefined || tbdLabel === null) {
    tbdLabel = DOLLAR_TBD;
  }
  if (tbdLabel.startsWith("$")) {
    return tbdLabel.slice(1);
  }
  return tbdLabel;
}

export function getTbdPriceLabel(tbdPriceLabel = DOLLAR_TBD) {
  if (tbdPriceLabel === undefined || tbdPriceLabel === null) {
    return DOLLAR_TBD;
  }
  return tbdPriceLabel;
}

/**
 * Returns the option with any GENERATE-intersection add-on (see `getGenerateIntersectionPriceDelta`)
 * added to its price, so a single `getOptionPriceDisplay` call shows base + add-ons. Returns the
 * option unchanged when nothing is generated.
 */
function applyGenerateIntersectionPriceDelta(
  optionsState: OptionsState,
  option: DocOption,
  optionChoices: OptionChoices,
): DocOption {
  const generateDelta = getGenerateIntersectionPriceDelta(optionsState, option, optionChoices);
  return generateDelta !== 0 ? { ...option, price: optionPrice(option) + generateDelta } : option;
}

export function getOptionPriceDisplayWithChoices(
  option: DocOption,
  optionSets: Table<DocOptionSet>,
  optionChoices: OptionChoices = {},
  tbdPriceLabel: string | undefined,
  priceIsHidden: boolean,
  includedLabel = "Included",
  instanceOptionSets: Table<InstanceOptionSet> = EMPTY_INSTANCE_OPTION_SETS,
): string {
  const optionsState = calculateOptionsState(
    { optionSets, instanceOptionSets },
    {
      ...optionChoices,
      [option.parentId]: option.id,
    },
    true,
    option.parentId,
  );

  const resolved = optionsState.active[option.parentId] || option;
  const pricedOption = applyGenerateIntersectionPriceDelta(optionsState, resolved, optionChoices);

  return getOptionPriceDisplay(pricedOption, tbdPriceLabel, priceIsHidden, optionSets, includedLabel);
}

/**
 * Builds option choices that represent "this option is selected."
 *
 * `doc` is expected except during initial plan load (and in tests); without it we can't preprocess
 * and just force the option. The real fork is `cascadeSensitiveSetIds` — sets whose row can change
 * under the option-rule cascade (a linked option's MODIFY/GENERATE target, or a set with a cross-set
 * formula price). Those run the full preprocessing (`prepareShowroomOptionChoices`: rule cascade +
 * sanitize) so sibling sets take on their selected-state values; every other set just forces the
 * option, skipping the costly cascade on the per-row hot path.
 */
export function getPreviewOptionChoicesForOption(
  option: DocOption,
  optionChoices: OptionChoices,
  doc: Doc | undefined,
  optionStateV2: boolean,
  cascadeSensitiveSetIds?: ReadonlySet<string>,
): OptionChoices {
  const modifiedChoices: OptionChoices = { [option.parentId]: option.id };
  // No doc, or this set's row can't change under the cascade: skip it and just force the option.
  if (!doc || (cascadeSensitiveSetIds && !cascadeSensitiveSetIds.has(option.parentId))) {
    return { ...optionChoices, ...modifiedChoices };
  }
  const prepared = prepareShowroomOptionChoices(doc, optionChoices, modifiedChoices, optionStateV2);
  // `sanitize` drops any choice equal to its set's default, so a zero-price default (e.g. 2-car)
  // would be stripped and never show up in `active`. Re-apply the forced selection so the previewed
  // option always resolves.
  return { ...prepared, ...modifiedChoices };
}

/**
 * Resolves an intro row's description and price "as if this option were selected." Both come from a
 * single `calculateOptionsState` pass over `previewChoices`, projecting active display data only
 * for this row's set. Both fields still agree on the same intersecting combo. `previewChoices`
 * must already include this option (see
 * `getPreviewOptionChoicesForOption`).
 */
export function getOptionRowDisplayWithChoices(
  option: DocOption,
  optionSets: Table<DocOptionSet>,
  previewChoices: OptionChoices,
  tbdPriceLabel: string | undefined,
  priceIsHidden: boolean,
  includedLabel = "Included",
  instanceOptionSets: Table<InstanceOptionSet> = EMPTY_INSTANCE_OPTION_SETS,
): {
  description: string | undefined;
  salesImages: DocOption["salesImages"];
  price: number;
  priceDisplay: string;
} {
  const definitions = { optionSets, instanceOptionSets };
  const optionsState = calculateOptionsState(definitions, previewChoices, true, option.parentId);
  const resolved = optionsState.active[option.parentId] ?? option;

  // If a lock or rule made the set resolve to a different option, keep this row's own description
  // rather than the other option's.
  const description = resolved.id === option.id ? resolved.description ?? option.description : option.description;
  const salesImages = resolved.id === option.id ? resolved.salesImages ?? option.salesImages : option.salesImages;

  const pricedOption = applyGenerateIntersectionPriceDelta(optionsState, resolved, previewChoices);
  // `optionPrice` is typed `number` but casts an unmatched MatchExpression result, so a formula price
  // can surface as undefined/NaN. Coerce here or callers summing this field produce NaN totals.
  const resolvedPrice = optionPrice(pricedOption, previewChoices);
  const price = Number.isFinite(resolvedPrice) ? resolvedPrice : 0;
  const priceDisplay = getOptionPriceDisplay(pricedOption, tbdPriceLabel, priceIsHidden, optionSets, includedLabel);

  return { description, salesImages, price, priceDisplay };
}

export default function getOptionPriceDisplay(
  option: DocOption,
  tbdPriceLabel: string | undefined,
  priceIsHidden: boolean,
  optionSets: Table<DocOptionSet>,
  includedLabel = "Included",
) {
  if (priceIsHidden) {
    return "";
  }

  return getOptionPriceDisplayForSet(
    optionPrice(option),
    option,
    optionSets[option.parentId],
    tbdPriceLabel,
    priceIsHidden,
    includedLabel,
  );
}

export function getOptionPriceDisplayForSet(
  price: number | undefined,
  option: DocOption,
  optionSet: DocOptionSet,
  tbdPriceLabel: string | undefined,
  priceIsHidden: boolean,
  includedLabel = "Included",
) {
  if (priceIsHidden) {
    return "";
  }

  const priceTbd = isBooleanOptionSet(optionSet) ? optionSet?.options.true.priceTbd : option.priceTbd;
  const isBaseOption = !!optionSet && option.id === optionSet.options[optionSet.defaultValue]?.id;

  return renderDisplayPrice(
    {
      value: price,
      tbd: priceTbd,
    },
    isBaseOption,
    tbdPriceLabel,
    includedLabel,
  );
}

export function renderDisplayPrice(
  price: {
    value?: number;
    tbd?: boolean;
  },
  isBaseOption: boolean,
  tbdPriceLabel: string | undefined,
  includedLabel = "Included",
  omitPlus?: boolean,
) {
  if (price.tbd && !price.value) {
    return getTbdPriceLabel(tbdPriceLabel);
  }
  if (price.value === 0 && isBaseOption) {
    return includedLabel;
  }
  if (price.value === undefined) {
    return "--";
  }
  return formatPricePlusMinus(price.value, omitPlus);
}
